import { type CSSProperties, useEffect } from 'react';

import { InformationCircleIcon } from '@heroicons/react/20/solid';

import { currencyFormatter } from '@/utils/currencyFormatter';
import { dateFormatter } from '@/utils/dateFormatter';

import type { GroupSubscriptionData } from '../groupSubscriptionTypes';
import { computeProratedCents } from './seatMath';

type AddSeatsAndInviteConfirmModalProps = {
  isOpen: boolean;
  group: GroupSubscriptionData | null;
  /** Number of seats to add — drives the math + copy throughout. */
  extraSeats: number;
  /** Emails to invite after the seat purchase confirms. */
  emails: string[];
  onCancel: () => void;
  /**
   * Real wiring: `InitiateSeatAddition` (BEE-18935) → `CreateInvitation`
   * (BEE-18931). For now caller closes the modal as a no-op.
   */
  onSubmit?: (groupId: string, extraSeats: number, emails: string[]) => void;
  onOpenChange: (open: boolean) => void;
  isLoading?: boolean;
};

export function AddSeatsAndInviteConfirmModal({
  isOpen,
  group,
  extraSeats,
  emails,
  onCancel,
  onSubmit,
  onOpenChange,
  isLoading = false,
}: AddSeatsAndInviteConfirmModalProps) {
  // ESC closes. Outside-click intentionally NOT bound — same pattern as
  // ManageSeatsModal/InviteByEmailModal: a stray overlay click shouldn't
  // dump the user back to a state where their typed emails are gone.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onOpenChange]);

  if (!isOpen || !group || extraSeats <= 0 || !group.renews_at) return null;

  const formatMoney = (cents: number) => currencyFormatter({ value: cents / 100, currency: group.currency });
  // Force UTC — renews_at is a calendar date stored as UTC midnight.
  const renewsOnLabel = dateFormatter({ timeZone: 'UTC' }).format(new Date(group.renews_at));
  const seatLabel = extraSeats === 1 ? 'seat' : 'seats';
  const newTotalSeats = group.max_seats + extraSeats;
  const newAnnualCents = newTotalSeats * group.per_seat_amount_cents;
  const proratedCents = computeProratedCents(group, extraSeats);

  const overlayStyle: CSSProperties = { backgroundColor: '#00000030' };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-seats-confirm-title"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={overlayStyle}
    >
      <div className="flex w-full flex-col gap-6 overflow-hidden bg-white p-6 shadow-lg sm:w-[600px] sm:rounded-md">
        <div className="flex flex-col gap-2">
          <h1 id="add-seats-confirm-title" className="text-lg font-medium text-gray-900">
            Add {extraSeats} {seatLabel} and send invites
          </h1>
          <p className="text-sm text-gray-500">
            You&rsquo;re adding {extraSeats} {seatLabel} to this group subscription. These seats will be available
            immediately, and invites will be sent after confirmation.
          </p>
        </div>

        <div className="rounded-lg bg-gray-50 p-4 text-sm">
          <dl className="flex flex-col gap-3">
            <div className="flex items-center justify-between text-gray-500">
              <dt>Seats added</dt>
              <dd>
                {extraSeats} {seatLabel}
              </dd>
            </div>
            <div className="flex items-center justify-between text-gray-500">
              <dt>Total seats</dt>
              <dd>{newTotalSeats} seats</dd>
            </div>
            <div className="flex items-center justify-between text-gray-500">
              <dt>Price per seat</dt>
              <dd>
                {formatMoney(group.per_seat_amount_cents)}/{group.interval}
              </dd>
            </div>
            <div className="mt-1 flex items-center justify-between border-t border-gray-200 pt-4">
              <dt className="font-medium text-gray-900">Annual total starting next renewal on {renewsOnLabel}</dt>
              <dd className="text-lg font-medium text-gray-900">
                {formatMoney(newAnnualCents)}/{group.interval}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="font-medium text-gray-900">Prorated charge today (estimate)</dt>
              <dd className="text-lg font-medium text-gray-900">{formatMoney(proratedCents)}</dd>
            </div>
          </dl>
        </div>

        <div className="flex gap-3 rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
          <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-500" aria-hidden="true" />
          <div className="flex flex-col gap-1">
            <p className="font-medium">
              Adding {extraSeats} {seatLabel}
            </p>
            <p>
              This change takes effect immediately. A prorated charge for the rest of your current billing period will
              be applied today.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            disabled={isLoading}
            onClick={onCancel}
            className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isLoading}
            onClick={() => onSubmit?.(group.id, extraSeats, emails)}
            className="rounded-md bg-wt-primary px-3 py-2 text-sm font-medium text-wt-text-on-primary shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:hover:opacity-100"
          >
            Confirm changes
          </button>
        </div>
      </div>
    </div>
  );
}
